import { ConsoleContinue } from "components/platform/companyConsole/ConsoleState";
import React from "react";
import { useQuery } from "@apollo/client";
import { Alert, Box, Button, Tab, Tabs, Typography } from "@mui/material";
import { Chip, DataTable, Drawer } from "@outpave/ds";
import type { DataColumn } from "@outpave/ds";
import { useTranslation } from "react-i18next";
import {
  COMPANY_REQUEST,
  COMPANY_REQUESTS,
  COMPANY_EVENTS,
  COMPANY_EVENT,
} from "graphql/companyConsole";
import { useCompanyConnection } from "hooks/useCompanyConnection";
import { Stack } from "components/layout/reusableLayouts";
import Summary from "components/common/Summary";
import EventLogs from "pages/platform/EventLogs";
import { ConsoleError } from "./ConsoleState";
import { ConsoleProps, dateFilter, instant, label } from "./types";

interface RequestRecord {
  id: string;
  companyId: string;
  startedAt: number;
  durationMs: number;
  method: string;
  route: string;
  operationName?: string;
  httpStatus: number;
  errorCode?: string;
  actorId?: string;
  operationDigest?: string;
  operationRequestId?: string;
  resourceId?: string;
  resourceType?: string;
  replayed?: boolean;
  operation?: {
    id: string;
    status: string;
    step?: string;
    updatedAt?: number;
    retryAt?: number;
    attempts: number;
  };
}
interface PublicEvent {
  id: string;
  type: string;
  createdAt: string;
  requestId?: string;
  resourceId: string;
  resourceType: string;
  transition: string;
}

function resourceTarget(
  type: string | undefined,
  id: string | undefined,
): Record<string, string> | null {
  if (!id) return null;
  if (["transaction", "transfer"].includes(type ?? ""))
    return { tab: "transactions", transaction: id };
  if (type === "card") return { tab: "cards", card: id };
  if (type === "financialAccount") return { tab: "accounts", account: id };
  return null;
}

export function CompanyActivity(props: ConsoleProps) {
  const { companyId, params, navigate } = props;
  const { t } = useTranslation();
  const section = params.get("activity") || "requests";
  const dates = dateFilter(params);
  const resourceId = params.get("resource") || undefined;
  return (
    <Stack gap="var(--space-3)" sx={{ flex: 1, minHeight: 0 }}>
      <Tabs
        aria-label={t("Activity sections")}
        value={section}
        onChange={(_event, value: string) =>
          navigate({ activity: value, request: null })
        }
      >
        <Tab value="requests" label={t("API requests")} />
        <Tab value="audit" label={t("Audit trail")} />
        <Tab value="events" label={t("Events")} />
        <Tab value="webhooks" label={t("Webhook deliveries")} />
      </Tabs>
      {section === "requests" && <Requests {...props} />}
      {section === "events" && <Events {...props} />}
      {["audit", "webhooks"].includes(section) && (
        <EventLogs
          key={section}
          embedded
          selectedCompanyId={companyId}
          view={section === "audit" ? "activity" : "webhooks"}
          resourceId={resourceId}
          eventId={params.get("event") || undefined}
          {...dates}
        />
      )}
    </Stack>
  );
}

function Requests({ companyId, params, navigate }: ConsoleProps) {
  const { t } = useTranslation();
  const page = useCompanyConnection<RequestRecord>({
    query: COMPANY_REQUESTS,
    field: "companyRequests",
    variables: {
      companyId,
      filter: {
        ...dateFilter(params),
        requestId: params.get("requestFilter") || undefined,
        operationDigest: params.get("operation") || undefined,
        resourceId: params.get("resource") || undefined,
        route: params.get("route") || undefined,
        result: params.get("result") || undefined,
      },
    },
  });
  const requestId = params.get("request");
  const detail = useQuery<{ companyRequest: RequestRecord }>(COMPANY_REQUEST, {
    variables: { companyId, requestId },
    skip: !requestId,
    fetchPolicy: "no-cache",
  });
  const record =
    detail.data?.companyRequest.id === requestId
      ? detail.data.companyRequest
      : undefined;
  const target = resourceTarget(record?.resourceType, record?.resourceId);
  const columns: DataColumn<RequestRecord>[] = [
    { id: "time", header: t("When"), cell: (row) => instant(row.startedAt) },
    {
      id: "route",
      header: t("Request"),
      cell: (row) => ({
        content: `${row.method} ${row.route}`,
        caption: row.id,
      }),
    },
    {
      id: "status",
      header: t("Response"),
      cell: (row) => (
        <Chip tone={row.httpStatus >= 400 ? "negative" : "positive"}>
          {row.httpStatus}
        </Chip>
      ),
    },
    {
      id: "duration",
      header: t("Duration"),
      cell: (row) => `${row.durationMs} ms`,
    },
    {
      id: "error",
      header: t("Error code"),
      cell: (row) => row.errorCode || "",
    },
    {
      id: "replay",
      header: t("Idempotent replay"),
      cell: (row) => t(row.replayed ? "Yes" : "No"),
    },
  ];
  return (
    <Stack gap="var(--space-3)" sx={{ flex: 1, minHeight: 0 }}>
      {page.error && <ConsoleError retry={page.retry} />}
      <ConsoleContinue paused={page.paused} loadMore={page.loadMore} />
      {page.connection?.available === false && (
        <Alert severity="info">
          {t("API request history has not been enabled for this environment")}
        </Alert>
      )}
      <Box ref={page.viewportRef} sx={{ flex: 1, minHeight: 0 }}>
        <DataTable
          columns={columns}
          rows={page.rows}
          getRowKey={(row) => row.id}
          loading={page.loading}
          isFetchingMore={page.isFetchingMore}
          onScroll={page.handleScroll}
          onRowClick={(row) => navigate({ request: row.id })}
          stickyHeader
          emptyState={t("No requests found")}
        />
      </Box>
      <Drawer
        open={!!requestId}
        onClose={() => navigate({ request: null })}
        title={t("API request")}
        loading={detail.loading}
      >
        {detail.error && <ConsoleError />}
        {record && (
          <Stack gap="var(--space-5)">
            <Summary
              disableContainer
              items={[
                { title: t("Request ID"), value: record.id, fullWidth: true },
                {
                  title: t("Request"),
                  value: `${record.method} ${record.route}`,
                  fullWidth: true,
                },
                { title: t("Response"), value: record.httpStatus },
                { title: t("Duration"), value: `${record.durationMs} ms` },
                { title: t("When"), value: instant(record.startedAt) },
                {
                  title: t("Actor / API key"),
                  value: record.actorId || t("Unavailable"),
                },
                {
                  title: t("Error code"),
                  value: record.errorCode || t("None"),
                },
                {
                  title: t("Operation ID"),
                  value: record.operationDigest || t("No durable operation"),
                  fullWidth: true,
                },
              ]}
            />
            {record.operation ? (
              <Summary
                disableContainer
                items={[
                  {
                    title: t("Operation status"),
                    value: label(record.operation.status),
                  },
                  {
                    title: t("Recovery attempts"),
                    value: record.operation.attempts,
                  },
                  {
                    title: t("Last step"),
                    value: label(record.operation.step),
                  },
                  {
                    title: t("Next recovery attempt"),
                    value: instant(record.operation.retryAt),
                  },
                ]}
              />
            ) : (
              record.operationDigest && (
                <Alert severity="info">
                  {t("Operation details are no longer available")}
                </Alert>
              )
            )}
            <Typography variant="body2">
              {t(
                "Operation status is independent of webhook delivery, Financial operations cannot be replayed here",
              )}
            </Typography>
            {target && (
              <Button
                variant="default-outlined"
                onClick={() => navigate({ ...target, request: null })}
              >
                {t("View resource")}
              </Button>
            )}
            {record.operationDigest && (
              <Button
                variant="default-outlined"
                onClick={() =>
                  navigate({
                    operation: record.operationDigest!,
                    request: null,
                    requestFilter: null,
                  })
                }
              >
                {t("All attempts for this operation")}
              </Button>
            )}
            <Button
              variant="default-outlined"
              onClick={() =>
                navigate({
                  activity: "events",
                  requestFilter: record.operationRequestId || record.id,
                  request: null,
                })
              }
            >
              {t("Related events")}
            </Button>
          </Stack>
        )}
      </Drawer>
    </Stack>
  );
}

function Events({ companyId, params, navigate }: ConsoleProps) {
  const { t } = useTranslation();
  const eventId = params.get("eventDetail");
  const detail = useQuery<{ companyEvent: PublicEvent }>(COMPANY_EVENT, {
    variables: { companyId, eventId },
    skip: !eventId,
    fetchPolicy: "no-cache",
  });
  const selected =
    detail.data?.companyEvent.id === eventId
      ? detail.data.companyEvent
      : undefined;
  const setSelected = (event: PublicEvent | null) =>
    navigate({ eventDetail: event?.id ?? null });
  const page = useCompanyConnection<PublicEvent>({
    query: COMPANY_EVENTS,
    field: "companyEvents",
    variables: {
      companyId,
      ...dateFilter(params),
      resourceId: params.get("resource") || undefined,
      requestId: params.get("requestFilter") || undefined,
    },
  });
  const columns: DataColumn<PublicEvent>[] = [
    { id: "time", header: t("When"), cell: (row) => instant(row.createdAt) },
    {
      id: "type",
      header: t("Event"),
      cell: (row) => ({ content: row.type, caption: row.id }),
    },
    {
      id: "resource",
      header: t("Resource"),
      cell: (row) => ({
        content: label(row.resourceType),
        caption: row.resourceId,
      }),
    },
    {
      id: "request",
      header: t("Request ID"),
      cell: (row) => row.requestId || t("Historical correlation unavailable"),
    },
  ];
  const target = resourceTarget(selected?.resourceType, selected?.resourceId);
  return (
    <Box ref={page.viewportRef} sx={{ flex: 1, minHeight: 0 }}>
      {page.error && <ConsoleError retry={page.retry} />}
      <ConsoleContinue paused={page.paused} loadMore={page.loadMore} />
      <DataTable
        columns={columns}
        rows={page.rows}
        getRowKey={(row) => row.id}
        loading={page.loading}
        isFetchingMore={page.isFetchingMore}
        onScroll={page.handleScroll}
        onRowClick={setSelected}
        stickyHeader
        emptyState={t("No events found")}
      />
      <Drawer
        loading={detail.loading}
        open={!!eventId}
        onClose={() => setSelected(null)}
        title={t("Event")}
      >
        {detail.error && <ConsoleError />}
        {selected && (
          <Stack gap="var(--space-4)">
            <Typography>{selected.type}</Typography>
            <Typography sx={{ overflowWrap: "anywhere" }}>
              {selected.id}
            </Typography>
            <Typography>{instant(selected.createdAt)}</Typography>
            <Box
              component="pre"
              sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere" }}
            >
              {JSON.stringify(JSON.parse(selected.transition), null, 2)}
            </Box>
            {target && (
              <Button
                variant="default-outlined"
                onClick={() => navigate(target)}
              >
                {t("View resource")}
              </Button>
            )}
            {selected.requestId && (
              <Button
                variant="default-outlined"
                onClick={() =>
                  navigate({
                    activity: "requests",
                    request: selected.requestId!,
                  })
                }
              >
                {t("View request")}
              </Button>
            )}
            <Button
              variant="default-outlined"
              onClick={() =>
                navigate({ activity: "webhooks", event: selected.id })
              }
            >
              {t("Webhook deliveries")}
            </Button>
          </Stack>
        )}
      </Drawer>
    </Box>
  );
}
